import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, FileText, Upload, Brain, Send, Download, CheckCircle2, Clock, AlertCircle, Phone, Mail, Calendar, Trash2, Eye } from 'lucide-react';
import { clientsApi, documentsApi, aiApi, disputesApi, itemsApi, API_BASE } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import InsightsPanel from './InsightsPanel';

const ItemStatus = ({ status }) => {
  const map = {
    removed: { label: 'Removed', cls: 'text-[#7fb685] border-[#7fb685]/40 bg-[#7fb685]/10', Icon: CheckCircle2 },
    disputed: { label: 'Disputed', cls: 'text-[#4b5320] border-[#4b5320]/40 bg-[#4b5320]/10', Icon: Clock },
    pending: { label: 'Pending', cls: 'text-stone-700 border-stone-300 bg-stone-100', Icon: AlertCircle },
    analyzed: { label: 'Analyzed', cls: 'text-[#4b5320] border-[#4b5320]/40 bg-[#4b5320]/10', Icon: CheckCircle2 },
    uploaded: { label: 'Uploaded', cls: 'text-stone-700 border-stone-300 bg-stone-100', Icon: CheckCircle2 },
    verified: { label: 'Verified', cls: 'text-[#7fb685] border-[#7fb685]/40 bg-[#7fb685]/10', Icon: CheckCircle2 },
  };
  const s = map[status] || map.pending;
  const Icon = s.Icon;
  return (
    <div className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-widest px-2.5 py-1 rounded-sm border ${s.cls}`}>
      <Icon className="w-3 h-3" />
      {s.label}
    </div>
  );
};

const DisputeStatus = ({ status }) => {
  const map = {
    awaiting_response: { label: 'Awaiting', cls: 'text-[#4b5320] border-[#4b5320]/40 bg-[#4b5320]/10' },
    resolved: { label: 'Resolved', cls: 'text-[#7fb685] border-[#7fb685]/40 bg-[#7fb685]/10' },
    rejected: { label: 'Rejected', cls: 'text-red-400 border-red-500/40 bg-red-500/10' },
  };
  const s = map[status] || map.awaiting_response;
  return <div className={`inline-flex items-center text-xs uppercase tracking-widest px-2.5 py-1 rounded-sm border ${s.cls}`}>{s.label}</div>;
};

const ScoreCard = ({ label, value, highlight }) => (
  <div className={`p-4 border rounded-sm ${highlight ? 'border-[#4b5320]/40 bg-[#4b5320]/5' : 'border-stone-200 bg-[#f7f6f0]'}`}>
    <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">{label}</div>
    <div className={`font-display text-2xl ${highlight ? 'text-[#4b5320]' : 'text-stone-900'}`}>{value || '—'}</div>
  </div>
);

const Metric = ({ label, value, accent }) => (
  <div className="border-l-2 border-stone-200 pl-4">
    <div className="text-xs uppercase tracking-widest text-stone-500">{label}</div>
    <div className={`font-display text-2xl mt-1 ${accent ? 'text-[#7fb685]' : 'text-stone-900'}`}>{value}</div>
  </div>
);

const ClientDetail = () => {
  const { id } = useParams();
  const { toast } = useToast();
  const [client, setClient] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeProgress, setAnalyzeProgress] = useState(0);
  const [selectedItems, setSelectedItems] = useState([]);
  const [letterOpen, setLetterOpen] = useState(false);
  const [activeLetter, setActiveLetter] = useState(null);
  const [bureau, setBureau] = useState('Equifax');

  const load = async () => {
    try {
      const r = await clientsApi.get(id);
      setClient(r.data);
    } catch (e) {
      toast({ title: 'Error', description: 'Could not load client' });
    }
  };

  useEffect(() => { load(); }, [id]);

  const runAnalysis = async () => {
    setAnalyzing(true);
    setAnalyzeProgress(0);
    // Smooth progress simulation: tick up to 90% over ~90s while we wait for the job
    const progressInt = setInterval(() => {
      setAnalyzeProgress(p => Math.min(p + 1, 90));
    }, 1000);
    try {
      const start = await aiApi.analyze(id);
      const jobId = start.data.jobId;
      if (!jobId) throw new Error('No job id returned');

      // Poll every 3s up to 5 minutes
      const result = await new Promise((resolve, reject) => {
        let attempts = 0;
        const maxAttempts = 100;
        const tick = async () => {
          attempts += 1;
          try {
            const r = await aiApi.getAnalyzeJob(id, jobId);
            if (r.data.status === 'complete') resolve(r.data.result);
            else if (r.data.status === 'failed') reject(new Error(r.data.error || 'Analysis failed'));
            else if (attempts >= maxAttempts) reject(new Error('Timed out after 5 minutes.'));
            else setTimeout(tick, 3000);
          } catch (e) {
            if (attempts >= maxAttempts) reject(e);
            else setTimeout(tick, 3000);
          }
        };
        tick();
      });

      setAnalyzeProgress(100);
      toast({ title: 'Analysis complete', description: `${result.count} disputable items identified.` });
      await load();
    } catch (e) {
      toast({ title: 'Analysis failed', description: e.response?.data?.detail || e.message || 'AI error' });
    } finally {
      clearInterval(progressInt);
      setTimeout(() => { setAnalyzing(false); setAnalyzeProgress(0); }, 800);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await documentsApi.upload(id, file, 'credit_report', null);
      toast({ title: 'Document uploaded', description: file.name });
      e.target.value = '';
      load();
    } catch (err) {
      toast({ title: 'Upload failed', description: err.response?.data?.detail || 'Could not upload' });
    }
  };

  const deleteDoc = async (docId) => {
    try {
      await documentsApi.delete(docId);
      toast({ title: 'Document deleted' });
      load();
    } catch {
      toast({ title: 'Could not delete' });
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('Delete this AI-extracted item? Use this to remove duplicates or wrong entries before disputing.')) return;
    try {
      await itemsApi.delete(itemId);
      toast({ title: 'Item deleted' });
      setSelectedItems(prev => prev.filter(id => id !== itemId));
      load();
    } catch (e) {
      toast({ title: 'Could not delete', description: e.response?.data?.detail || 'Try again.' });
    }
  };

  const editAddress = async () => {
    const current = client?.address || '';
    const next = window.prompt('Mailing address (used on dispute letters):', current);
    if (next === null) return;
    try {
      await clientsApi.update(id, { address: next.trim() });
      toast({ title: 'Address updated' });
      load();
    } catch (e) {
      toast({ title: 'Failed', description: e.response?.data?.detail || 'Could not update address' });
    }
  };

  const toggleItem = (itemId) => {
    setSelectedItems(prev => prev.includes(itemId) ? prev.filter(i => i !== itemId) : [...prev, itemId]);
  };

  const generateDispute = async () => {
    if (selectedItems.length === 0) {
      toast({ title: 'No items selected', description: 'Select disputable items first.' });
      return;
    }
    try {
      toast({ title: 'Generating letter...', description: 'AI is drafting your dispute.' });
      const r = await disputesApi.create(id, { itemIds: selectedItems, bureau, type: 'Initial Dispute' });
      setActiveLetter(r.data);
      setLetterOpen(true);
      setSelectedItems([]);
      toast({ title: 'Dispute generated', description: `Round ${r.data.round} letter ready for ${r.data.bureau}.` });
      load();
    } catch (e) {
      // 402 = payment required
      if (e.response?.status === 402) {
        toast({
          title: 'Payment method required',
          description: 'Set up billing to start disputing. Redirecting to billing...',
        });
        setTimeout(() => { window.location.href = '/app/billing'; }, 1500);
        return;
      }
      toast({ title: 'Failed', description: e.response?.data?.detail || 'Could not generate' });
    }
  };

  const viewLetter = async (disputeId) => {
    try {
      const r = await disputesApi.get(disputeId);
      setActiveLetter(r.data);
      setLetterOpen(true);
    } catch {
      toast({ title: 'Could not load letter' });
    }
  };

  const downloadPdf = (disputeId) => {
    const token = localStorage.getItem('infantry_token');
    fetch(`${API_BASE}/disputes/${disputeId}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dispute_${disputeId}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast({ title: 'PDF downloaded' });
      })
      .catch(() => toast({ title: 'Download failed' }));
  };

  if (!client) {
    return <div className="p-8 text-center text-stone-500">Loading client...</div>;
  }

  const items = client.negativeItemsList || [];
  const documents = client.documentsList || [];
  const disputes = client.disputesList || [];
  const lift = (client.currentScore || 0) - (client.initialScore || 0);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <Link to="/app/clients" className="inline-flex items-center gap-2 text-sm text-stone-700 hover:text-stone-900 mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to clients
      </Link>

      <div className="border border-stone-200/80 bg-[#ffffff] rounded-sm p-8 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 bg-[#4b5320]/15 border border-[#4b5320]/40 text-[#4b5320] flex items-center justify-center rounded-sm font-display text-2xl">
              {client.avatar}
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-1">Client File · {client.id.slice(0, 8).toUpperCase()}</div>
              <h1 className="font-display text-3xl text-stone-900">{client.name}</h1>
              <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-stone-700">
                <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {client.email}</span>
                {client.phone && <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {client.phone}</span>}
                <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Enrolled {client.enrolledDate}</span>
              </div>
              {client.address ? (
                <div className="text-sm text-stone-700 mt-1.5 flex items-start gap-1.5" data-testid="client-address-display">
                  <span className="text-xs uppercase tracking-widest text-stone-500 mt-0.5">Mailing:</span> {client.address}
                </div>
              ) : (
                <button
                  onClick={editAddress}
                  className="text-xs text-[#4b5320] hover:underline mt-1.5"
                  data-testid="client-add-address-btn"
                >
                  + Add mailing address (required for dispute letters)
                </button>
              )}
            </div>
          </div>
          <Button onClick={runAnalysis} disabled={analyzing} className="bg-[#4b5320] hover:bg-[#3d4419] text-white font-semibold rounded-sm">
            <Brain className="w-4 h-4 mr-2" /> {analyzing ? 'Analyzing...' : 'Run AI Analysis'}
          </Button>
        </div>

        {analyzing && (
          <div className="mt-6">
            <div className="flex items-center justify-between text-xs text-stone-700 mb-2">
              <span>Claude is analyzing credit report data...</span>
              <span className="font-mono text-[#4b5320]">{analyzeProgress}%</span>
            </div>
            <Progress value={analyzeProgress} className="h-1 bg-stone-200" />
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-8 pt-8 border-t border-stone-200/80">
          <ScoreCard label="Initial" value={client.initialScore} />
          <ScoreCard label="Current" value={client.currentScore} highlight />
          <ScoreCard label="Equifax" value={client.bureaus?.equifax} />
          <ScoreCard label="Experian" value={client.bureaus?.experian} />
          <ScoreCard label="TransUnion" value={client.bureaus?.transunion} />
        </div>

        <div className="grid grid-cols-3 gap-4 mt-4">
          <Metric label="Score Lift" value={lift > 0 ? `+${lift}` : lift} accent={lift > 0} />
          <Metric label="Items Removed" value={`${client.itemsRemoved}/${client.negativeItems}`} />
          <Metric label="Active Disputes" value={client.activeDisputes} />
        </div>
      </div>

      <Tabs defaultValue="analysis">
        <TabsList className="bg-[#ffffff] border border-stone-200 rounded-sm">
          <TabsTrigger value="analysis" className="data-[state=active]:bg-[#4b5320] data-[state=active]:text-white rounded-sm text-stone-700" data-testid="tab-analysis">AI Analysis</TabsTrigger>
          <TabsTrigger value="insights" className="data-[state=active]:bg-[#4b5320] data-[state=active]:text-white rounded-sm text-stone-700" data-testid="tab-insights">Insights</TabsTrigger>
          <TabsTrigger value="documents" className="data-[state=active]:bg-[#4b5320] data-[state=active]:text-white rounded-sm text-stone-700" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="disputes" className="data-[state=active]:bg-[#4b5320] data-[state=active]:text-white rounded-sm text-stone-700" data-testid="tab-disputes">Disputes</TabsTrigger>
        </TabsList>

        <TabsContent value="analysis" className="mt-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-1">Disputable Items by Bureau</div>
              <h2 className="font-display text-xl text-stone-900">AI-Identified Negative Items</h2>
              <p className="text-sm text-stone-500 mt-1">{items.length} total · {selectedItems.length} selected</p>
            </div>
            <div className="flex items-center gap-2">
              <select value={bureau} onChange={(e) => setBureau(e.target.value)} className="bg-[#fafaf6] border border-stone-200 text-stone-800 text-sm rounded-sm h-10 px-3" data-testid="dispute-bureau-select">
                <option>Equifax</option>
                <option>Experian</option>
                <option>TransUnion</option>
              </select>
              <Button onClick={generateDispute} disabled={selectedItems.length === 0} className="bg-[#4b5320] hover:bg-[#3d4419] text-white font-semibold rounded-sm disabled:opacity-40" data-testid="generate-dispute-btn">
                <Send className="w-4 h-4 mr-2" /> Generate Dispute ({selectedItems.length})
              </Button>
            </div>
          </div>
          {items.length === 0 ? (
            <div className="border border-stone-200/80 bg-[#ffffff] rounded-sm p-16 text-center text-stone-500">
              No items yet. Click &ldquo;Run AI Analysis&rdquo; to identify disputable items.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {['Equifax', 'Experian', 'TransUnion'].map((b) => {
                const bureauItems = items.filter(i => (i.bureau || '').toLowerCase() === b.toLowerCase());
                const bureauSelected = bureauItems.filter(i => selectedItems.includes(i.id)).length;
                return (
                  <div key={b} className="border border-stone-200/80 bg-[#ffffff] rounded-sm flex flex-col" data-testid={`bureau-column-${b.toLowerCase()}`}>
                    <div className="px-4 py-3 border-b border-stone-200/80 bg-[#4b5320]/5 flex items-center justify-between">
                      <div>
                        <div className="text-xs uppercase tracking-widest text-[#4b5320] font-bold">{b}</div>
                        <div className="text-xs text-stone-500 mt-0.5">{bureauItems.length} item{bureauItems.length !== 1 ? 's' : ''}{bureauSelected ? ` · ${bureauSelected} selected` : ''}</div>
                      </div>
                      {bureauItems.length > 0 && (
                        <button
                          onClick={() => {
                            const ids = bureauItems.filter(i => i.status !== 'removed').map(i => i.id);
                            const allSelected = ids.every(id => selectedItems.includes(id));
                            setSelectedItems(prev => allSelected ? prev.filter(id => !ids.includes(id)) : [...new Set([...prev, ...ids])]);
                          }}
                          className="text-[10px] uppercase tracking-wider text-[#4b5320] hover:underline"
                          data-testid={`bureau-select-all-${b.toLowerCase()}`}
                        >
                          {bureauItems.filter(i => i.status !== 'removed').every(i => selectedItems.includes(i.id)) ? 'Clear' : 'Select all'}
                        </button>
                      )}
                    </div>
                    <div className="divide-y divide-stone-200/80 flex-1">
                      {bureauItems.length === 0 && (
                        <div className="p-8 text-center text-xs text-stone-400 italic">No items on this bureau</div>
                      )}
                      {bureauItems.map(item => {
                        const checked = selectedItems.includes(item.id);
                        return (
                          <div key={item.id} className={`p-4 hover:bg-stone-100/40 transition-colors ${checked ? 'bg-[#4b5320]/5' : ''}`}>
                            <div className="flex items-start gap-3">
                              <button
                                onClick={() => toggleItem(item.id)}
                                disabled={item.status === 'removed'}
                                className={`w-4 h-4 mt-0.5 border rounded-sm flex items-center justify-center flex-shrink-0 transition-colors ${
                                  checked ? 'bg-[#4b5320] border-[#4b5320]' : 'border-stone-300 hover:border-stone-500'
                                } ${item.status === 'removed' ? 'opacity-30 cursor-not-allowed' : ''}`}
                              >
                                {checked && <CheckCircle2 className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                              </button>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start gap-2 mb-1">
                                  <div className="flex-1 min-w-0 flex flex-wrap items-center gap-1.5">
                                    <span className="font-semibold text-stone-900 text-sm truncate">{item.creditor}</span>
                                    <ItemStatus status={item.status} />
                                  </div>
                                  <button
                                    onClick={() => deleteItem(item.id)}
                                    disabled={item.status === 'disputed' || item.status === 'removed'}
                                    title={item.status === 'disputed' || item.status === 'removed' ? 'Already part of a dispute' : 'Delete this item'}
                                    className="text-stone-400 hover:text-red-600 disabled:opacity-20 disabled:cursor-not-allowed flex-shrink-0"
                                    data-testid={`delete-item-${item.id}`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                                <div className="flex flex-wrap items-center gap-1.5 mb-2 text-[10px]">
                                  <span className="font-mono text-stone-500">{item.accountNumber}</span>
                                  <span className="px-1.5 py-0.5 border border-stone-300 bg-stone-100 rounded-sm text-stone-700 uppercase tracking-wider">{item.type}</span>
                                </div>
                                <p className="text-xs text-stone-700 leading-relaxed">
                                  <span className="text-stone-500">Grounds:</span> {item.reason}
                                </p>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[10px] text-stone-500">
                                  {item.amount > 0 && <span>${item.amount.toLocaleString()}</span>}
                                  {item.dateReported && <span className="font-mono">{item.dateReported}</span>}
                                  <span className="flex items-center gap-1.5">
                                    <span className="font-mono text-[#4b5320]">{item.confidence}%</span>
                                    <div className="w-10 h-0.5 bg-stone-200 rounded-full overflow-hidden">
                                      <div className="h-full bg-[#4b5320]" style={{ width: `${item.confidence}%` }}></div>
                                    </div>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="insights" className="mt-6" data-testid="insights-tab-content">
          <InsightsPanel clientId={id} clientEmail={client.email} clientName={client.name} />
        </TabsContent>

        <TabsContent value="documents" className="mt-6">
          <div className="border border-stone-200/80 bg-[#ffffff] rounded-sm">
            <div className="flex items-center justify-between p-6 border-b border-stone-200/80">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-1">Vault</div>
                <h2 className="font-display text-xl text-stone-900">Client Documents</h2>
                <p className="text-sm text-stone-500 mt-1">{documents.length} files · Encrypted at rest</p>
              </div>
              <label className="cursor-pointer inline-flex items-center gap-2 bg-[#4b5320] hover:bg-[#3d4419] text-white font-semibold rounded-sm h-10 px-4 text-sm">
                <Upload className="w-4 h-4" /> Upload Document
                <input type="file" onChange={handleUpload} className="hidden" />
              </label>
            </div>
            <div className="divide-y divide-stone-200/80">
              {documents.length === 0 && (
                <div className="p-16 text-center text-stone-500">No documents yet. Upload credit reports to begin.</div>
              )}
              {documents.map(doc => (
                <div key={doc.id} className="flex items-center gap-4 p-5 hover:bg-stone-100/40 transition-colors">
                  <div className="w-10 h-10 bg-stone-100 border border-stone-200 flex items-center justify-center rounded-sm">
                    <FileText className="w-4 h-4 text-[#4b5320]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-stone-900 text-sm font-medium truncate">{doc.name}</div>
                    <div className="text-xs text-stone-500 mt-0.5">
                      {doc.bureau && <span>{doc.bureau} · </span>}
                      {doc.size} · Uploaded {doc.uploaded}
                    </div>
                  </div>
                  <ItemStatus status={doc.status} />
                  <a
                    href={documentsApi.fileUrl(doc.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-stone-500 hover:text-[#4b5320]"
                    title="View document"
                  >
                    <Eye className="w-4 h-4" />
                  </a>
                  <a
                    href={documentsApi.fileUrl(doc.id)}
                    download={doc.name}
                    className="text-stone-500 hover:text-[#4b5320]"
                    title="Download"
                  >
                    <Download className="w-4 h-4" />
                  </a>
                  <button onClick={() => deleteDoc(doc.id)} className="text-stone-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="disputes" className="mt-6">
          <div className="border border-stone-200/80 bg-[#ffffff] rounded-sm">
            <div className="p-6 border-b border-stone-200/80">
              <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-1">Operations Log</div>
              <h2 className="font-display text-xl text-stone-900">Active & Past Disputes</h2>
            </div>
            <div className="divide-y divide-stone-200/80">
              {disputes.length === 0 && (
                <div className="p-16 text-center text-stone-500">No disputes yet. Select items from AI analysis to generate one.</div>
              )}
              {disputes.map(d => (
                <div key={d.id} className="p-5 hover:bg-stone-100/40 transition-colors">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-3 mb-1.5">
                        <span className="font-display text-lg text-stone-900">Round {d.round}</span>
                        <span className="text-xs px-2 py-0.5 border border-stone-300 bg-stone-100 rounded-sm text-stone-700 uppercase tracking-wider">{d.bureau}</span>
                        <DisputeStatus status={d.status} />
                      </div>
                      <div className="text-sm text-stone-700">{d.type} · {d.items?.length || 0} item(s)</div>
                      <div className="text-xs text-stone-500 mt-1">Sent {d.dateSent}</div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => viewLetter(d.id)} variant="outline" className="border-stone-300 bg-transparent text-stone-700 hover:bg-stone-100 rounded-sm">
                        <FileText className="w-4 h-4 mr-2" /> View Letter
                      </Button>
                      <Button onClick={() => downloadPdf(d.id)} variant="outline" className="border-stone-300 bg-transparent text-stone-700 hover:bg-stone-100 rounded-sm">
                        <Download className="w-4 h-4 mr-2" /> PDF
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={letterOpen} onOpenChange={setLetterOpen}>
        <DialogContent className="bg-[#ffffff] border-stone-200 text-stone-800 rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Dispute Letter</DialogTitle>
          </DialogHeader>
          {activeLetter && (
            <div className="text-xs text-stone-700 mb-2">
              {activeLetter.bureau} · Round {activeLetter.round} · {activeLetter.type}
            </div>
          )}
          <div className="bg-[#f7f6f0] border border-stone-200 rounded-sm p-6 max-h-[480px] overflow-auto">
            <pre className="font-mono text-xs text-stone-700 whitespace-pre-wrap leading-relaxed">{activeLetter?.letter || 'No letter content.'}</pre>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLetterOpen(false)} className="border-stone-300 bg-transparent text-stone-700 hover:bg-stone-100 rounded-sm">Close</Button>
            <Button onClick={() => downloadPdf(activeLetter.id)} className="bg-[#4b5320] hover:bg-[#3d4419] text-white font-semibold rounded-sm">
              <Download className="w-4 h-4 mr-2" /> Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDetail;
